// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useMemo, useState } from 'react';
import { ShieldBan, MessageCircleMore, Megaphone, CalendarClock, Hourglass, LayoutGrid } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  fetchAtendabotReservasSettings,
  fetchRestaurantSettings,
  updateAtendabotReservasSettings,
  updateRestaurantSettings,
} from '../../services/api.ts';
import { toast } from 'react-toastify';
import { ATENDABOT_PRODUCT_IMAGE, PRODUCT_IMAGES } from '../../../../../branding/assets';
import AvailabilitySettingsTrigger from '../../components/admin/AvailabilitySettingsTrigger.tsx';
import BlockManagementModal from '../../components/admin/BlockManagementModal.tsx';
import { useAuthSession } from '../../../../../contexts/AuthSessionContext';
import ProductCheckoutModal from '../../../../billing/ProductCheckoutModal';
import '../../styles/admin-dashboard.css';
import '../../styles/settings-page.css';

const ATENDABOT_WHATSAPP_CONTRACT_URL = String(
  process.env.REACT_APP_ATENDABOT_WHATSAPP_CONTRACT_URL || '',
).trim(); 
const FEEDBACKHERO_WHATSAPP_CONTRACT_URL = String(
  process.env.REACT_APP_FEEDBACKHERO_WHATSAPP_CONTRACT_URL || '',
).trim();

type ReservoUsageMode = 'reservas' | 'fila' | 'ambos';

interface OccupancyReleaseConfirmation {
  count: number;
  source: 'mesas' | 'ambientes';
}

const openExternalLink = (url: string) => {
  const target = String(url || '').trim();
  if (!target || typeof window === 'undefined') return;
  window.open(target, '_blank', 'noopener,noreferrer');
};

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasProductAccess } = useAuthSession();
  const hasAtendabotProduct = hasProductAccess('atendabot');
  const hasFeedbackHeroProduct = hasProductAccess('feedback_hero');
  const hasAtendabotWhatsappContractLink = Boolean(ATENDABOT_WHATSAPP_CONTRACT_URL);
  const hasFeedbackHeroWhatsappContractLink = Boolean(FEEDBACKHERO_WHATSAPP_CONTRACT_URL);

  const [settings, setSettings] = useState({
    reservo_usar_reservas: true,
    reservo_usar_fila: false,
    reservo_usar_mapa_mesas: true,
    reservo_confirmacao_restaurante: false,
    reservo_confirmacao_cliente: false,
    reservo_prazo_reserva_dias: 30,
    reservo_intervalo_minutos: 30 as number | null,
    usar_feedbackhero_avaliacao_reservas: false,
  });
  const [atendabotReservas, setAtendabotReservas] = useState({
    usar_reservo_reservas: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [occupancyReleaseConfirmation, setOccupancyReleaseConfirmation] = useState<OccupancyReleaseConfirmation | null>(null);
  const [showBlockManager, setShowBlockManager] = useState(false);
  const [checkoutProductKey, setCheckoutProductKey] = useState<string | null>(null);
  const today = useMemo(() => new Date(), []);
  const usageMode = useMemo<ReservoUsageMode>(() => {
    if (settings.reservo_usar_reservas && settings.reservo_usar_fila) return 'ambos';
    if (settings.reservo_usar_fila) return 'fila';
    return 'reservas';
  }, [settings.reservo_usar_fila, settings.reservo_usar_reservas]);

  React.useEffect(() => {
    loadSettings();
    // Recarrega quando produtos relacionados mudam; omite `loadSettings` (recria a cada render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAtendabotProduct, hasFeedbackHeroProduct]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const reservoData = await fetchRestaurantSettings();

      setSettings((prev) => ({
        ...prev,
        ...(reservoData || {}),
        reservo_usar_reservas: reservoData?.reservo_usar_reservas !== false,
        reservo_usar_fila: reservoData?.reservo_usar_fila === true,
        reservo_usar_mapa_mesas: reservoData?.reservo_usar_mapa_mesas !== false,
        reservo_confirmacao_restaurante: Boolean(reservoData?.reservo_confirmacao_restaurante),
        reservo_confirmacao_cliente: Boolean(reservoData?.reservo_confirmacao_cliente),
        reservo_prazo_reserva_dias: Number.isFinite(Number(reservoData?.reservo_prazo_reserva_dias))
          ? Number(reservoData?.reservo_prazo_reserva_dias)
          : prev.reservo_prazo_reserva_dias,
        reservo_intervalo_minutos: reservoData?.reservo_intervalo_minutos === null
          ? null
          : (Number.isFinite(Number(reservoData?.reservo_intervalo_minutos))
            ? Number(reservoData?.reservo_intervalo_minutos)
            : prev.reservo_intervalo_minutos),
        usar_feedbackhero_avaliacao_reservas:
          hasFeedbackHeroProduct && reservoData?.usar_feedbackhero_avaliacao_reservas === true,
      }));
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      setLoading(false);
    }

    if (!hasAtendabotProduct) {
      return;
    }

    try {
      const atendabotData = await fetchAtendabotReservasSettings();
      setAtendabotReservas((prev) => ({
        ...prev,
        usar_reservo_reservas: Boolean(atendabotData?.usar_reservo_reservas),
      }));
    } catch (atendabotError: any) {
      console.error('Erro ao carregar integração com o Atendabot:', atendabotError);
    }
  };

  const handleSave = async (releaseActiveOccupancies = false) => {
    setSaving(true);
    try {
      const savedReservo = await updateRestaurantSettings({
        reservo_usar_reservas: settings.reservo_usar_reservas,
        reservo_usar_fila: settings.reservo_usar_fila,
        reservo_confirmacao_restaurante: settings.reservo_confirmacao_restaurante,
        reservo_confirmacao_cliente: settings.reservo_confirmacao_cliente,
        reservo_prazo_reserva_dias: settings.reservo_prazo_reserva_dias,
        reservo_intervalo_minutos: settings.reservo_intervalo_minutos,
        ...(releaseActiveOccupancies ? { liberar_ocupacoes_ativas: true } : {}),
        usar_feedbackhero_avaliacao_reservas:
          hasFeedbackHeroProduct && settings.usar_feedbackhero_avaliacao_reservas === true,
      });

      setSettings((prev) => ({
        ...prev,
        ...(savedReservo || {}),
        reservo_usar_reservas: savedReservo?.reservo_usar_reservas !== false,
        reservo_usar_fila: savedReservo?.reservo_usar_fila === true,
        reservo_usar_mapa_mesas: savedReservo?.reservo_usar_mapa_mesas !== false,
        reservo_confirmacao_restaurante: Boolean(savedReservo?.reservo_confirmacao_restaurante),
        reservo_confirmacao_cliente: Boolean(savedReservo?.reservo_confirmacao_cliente),
        reservo_prazo_reserva_dias: Number.isFinite(Number(savedReservo?.reservo_prazo_reserva_dias))
          ? Number(savedReservo?.reservo_prazo_reserva_dias)
          : prev.reservo_prazo_reserva_dias,
        reservo_intervalo_minutos: savedReservo?.reservo_intervalo_minutos === null
          ? null
          : (Number.isFinite(Number(savedReservo?.reservo_intervalo_minutos))
            ? Number(savedReservo?.reservo_intervalo_minutos)
            : prev.reservo_intervalo_minutos),
        usar_feedbackhero_avaliacao_reservas:
          hasFeedbackHeroProduct && savedReservo?.usar_feedbackhero_avaliacao_reservas === true,
      }));

      setOccupancyReleaseConfirmation(null);

      if (hasAtendabotProduct) {
        try {
          const payloadAtendabot = {
            usar_reservo_reservas: atendabotReservas.usar_reservo_reservas === true,
          };
          const savedAtendabot = await updateAtendabotReservasSettings(payloadAtendabot);

          setAtendabotReservas((prev) => ({
            ...prev,
            usar_reservo_reservas: Boolean(savedAtendabot?.usar_reservo_reservas),
          }));
        } catch (atendabotError: any) {
          console.error('Erro ao salvar integração com o Atendabot:', atendabotError);
          toast.error('Configurações do Reservo salvas, mas houve um erro ao salvar a integração com o Atendabot.');
        }
      }

      toast.success('Configurações salvas com sucesso!');
    } catch (error: any) {
      console.error('Erro ao salvar configurações:', error);
      const details = error?.response?.data?.details;
      if (
        error?.response?.status === 409
        && details?.code === 'RESERVO_MODO_POSSUI_OCUPACOES_ATIVAS'
      ) {
        setOccupancyReleaseConfirmation({
          count: Math.max(1, Number(details.ocupacoes_ativas) || 1),
          source: details.origem === 'ambientes' ? 'ambientes' : 'mesas',
        });
      } else {
        toast.error(error?.response?.data?.error || error?.response?.data?.message || 'Erro ao salvar configurações');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleChange = (field: string, value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUsageModeChange = (mode: ReservoUsageMode) => {
    setSettings((prev) => ({
      ...prev,
      reservo_usar_reservas: mode === 'reservas' || mode === 'ambos',
      reservo_usar_fila: mode === 'fila' || mode === 'ambos',
    }));
  };

  const handleChange = (field: string, value: number) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNullableNumberChange = (field: string, value: string) => {
    const trimmed = value.trim();
    const parsed = trimmed ? parseInt(trimmed, 10) : NaN;
    setSettings((prev) => ({
      ...prev,
      [field]: Number.isNaN(parsed) ? null : parsed,
    }));
  };

  if (loading) {
    return <div className="loading">Carregando configurações...</div>;
  }

  return (
    <div className="admin-dashboard settings-page">
      <div className="settings-page-header">
        <div className="settings-page-header-text">
          <h1>Preferências do sistema</h1>
          <p>Defina as regras principais do Reservo para o restaurante.</p>
        </div>

        <div className="settings-header-actions">
          <AvailabilitySettingsTrigger className="settings-header-btn" />
          <button
            type="button"
            className="settings-header-btn settings-header-btn--danger"
            onClick={() => setShowBlockManager(true)}
          >
            <ShieldBan size={16} />
            Bloquear Horário
          </button>
        </div>
      </div>

      <section className="room-overview-panel settings-panel">
        <div className="settings-form">
          <div className="form-section">
            <h3>Modo de uso do Reservo</h3>
            <div className="settings-mode-grid" role="radiogroup" aria-label="Modo de uso do Reservo">
              <button
                type="button"
                role="radio"
                aria-checked={usageMode === 'reservas'}
                className={`settings-mode-option ${usageMode === 'reservas' ? 'is-selected' : ''}`}
                onClick={() => handleUsageModeChange('reservas')}
              >
                <CalendarClock size={18} />
                <span className="settings-mode-title">Reservas</span>
              </button>

              <button
                type="button"
                role="radio"
                aria-checked={usageMode === 'fila'}
                className={`settings-mode-option ${usageMode === 'fila' ? 'is-selected' : ''}`}
                onClick={() => handleUsageModeChange('fila')}
              >
                <Hourglass size={18} />
                <span className="settings-mode-title">Fila de espera</span>
              </button>

              <button
                type="button"
                role="radio"
                aria-checked={usageMode === 'ambos'}
                className={`settings-mode-option ${usageMode === 'ambos' ? 'is-selected' : ''}`}
                onClick={() => handleUsageModeChange('ambos')}
              >
                <LayoutGrid size={18} />
                <span className="settings-mode-title">Reservas + fila</span>
              </button>
            </div>
          </div>

          <div className="form-section">
            <h3>Aprovação de Reservas</h3>
            <div className="settings-toggle-row">
              <div className="settings-toggle-text">
                <span className="settings-toggle-title">Confirmação pelo cliente via WhatsApp</span>
                <small>Quando ativo, reservas futuras pedem confirmação do cliente no WhatsApp; reservas do mesmo dia entram confirmadas.</small>
              </div>
              <div className="settings-toggle-control">
                <button
                  type="button"
                  role="switch"
                  aria-checked={settings.reservo_confirmacao_cliente}
                  aria-label="Confirmação pelo cliente via WhatsApp"
                  onClick={() => handleToggleChange('reservo_confirmacao_cliente', !settings.reservo_confirmacao_cliente)}
                  className={`settings-toggle ${settings.reservo_confirmacao_cliente ? 'is-on' : 'is-off'}`}
                >
                  <span className="settings-toggle-thumb" />
                </button>
                <span className={`settings-status ${settings.reservo_confirmacao_cliente ? 'is-on' : 'is-off'}`}>
                  {settings.reservo_confirmacao_cliente ? 'Ativo' : 'Inativo'}
                </span>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Prazo para Reservas</h3>
            <div className="settings-inputs-grid">
              <div className="form-group">
                <label>Quantidade máxima de dias</label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={settings.reservo_prazo_reserva_dias}
                  onChange={(e) => handleChange('reservo_prazo_reserva_dias', parseInt(e.target.value, 10))}
                />
                <small>Ex: 7 dias significa que reservas só podem ser feitas até 7 dias contando com hoje.</small>
              </div>
              <div className="form-group">
                <label>Intervalo entre reservas (minutos)</label>
                <input
                  type="number"
                  min="1"
                  max="240"
                  value={settings.reservo_intervalo_minutos ?? ''}
                  onChange={(e) => handleNullableNumberChange('reservo_intervalo_minutos', e.target.value)}
                  placeholder="30"
                />
                <small>Deixe vazio para usar o intervalo padrão (30 minutos).</small>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Atendabot + Reservo</h3>

            <div
              className="rounded-2xl border border-[#4bb17f]/40 p-5 text-white shadow-2xl"
              style={{ background: 'linear-gradient(135deg, #3127a0 0%, #2f8f6b 100%)' }}
            >
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-shrink-0 rounded-2xl border border-[#74c79b]/45 bg-[#3127A0] p-2 shadow-lg">
                  <img
                    src={ATENDABOT_PRODUCT_IMAGE}
                    alt="Atendabot"
                    className="h-16 w-16 rounded-xl bg-transparent p-1 object-contain"
                  />
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-sm font-semibold">Reservas por conversa com Atendabot</p>
                  <p className="text-xs text-white/90">
                    Controle nesta tela se o Atendabot pode executar reservas usando a disponibilidade do Reservo.
                  </p>
                  <p className="text-[11px] text-white/80">
                    Esta configuração é compartilhada com a aba Reservas do Prompt do Atendabot.
                  </p>
                </div>
                {hasAtendabotProduct ? (
                  <button
                    type="button"
                    onClick={() => navigate('/atendabot')}
                    className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-xl border border-[#74c79b]/55 bg-[#2b2d8f]/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-[#2b2d8f]/85"
                  >
                    <MessageCircleMore size={14} />
                    Abrir Atendabot
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setCheckoutProductKey('atendabot')}
                    className="ml-auto inline-flex shrink-0 items-center rounded-xl border border-[#74c79b]/55 bg-[#2b2d8f]/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-[#2b2d8f]/85 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Ativar Atendabot
                  </button>
                )}
              </div>
              {hasAtendabotProduct && (
                <div className="mt-5 rounded-xl border border-white/20 bg-black/15 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold">Permitir reservas pelo Atendabot</span>
                      <span className="mt-1 block text-xs text-white/85">
                        Quando ativo, a IA coleta data, horário, pessoas e contato para reservar.
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={atendabotReservas.usar_reservo_reservas}
                        aria-label="Permitir reservas pelo Atendabot"
                        onClick={() => setAtendabotReservas((prev) => ({
                          ...prev,
                          usar_reservo_reservas: !prev.usar_reservo_reservas,
                        }))}
                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:cursor-not-allowed disabled:opacity-60 ${
                          atendabotReservas.usar_reservo_reservas ? 'bg-emerald-400' : 'bg-white/30'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out mt-0.5 ${
                            atendabotReservas.usar_reservo_reservas ? 'translate-x-[1.375rem]' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        atendabotReservas.usar_reservo_reservas
                          ? 'bg-emerald-200 text-emerald-900'
                          : 'bg-slate-700 text-slate-100'
                      }`}>
                        {atendabotReservas.usar_reservo_reservas ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {!hasAtendabotProduct && (
                <div className="mt-4 space-y-3 rounded-xl border border-white/20 bg-black/15 p-4">
                  <p className="text-xs text-white/90">
                    Este restaurante ainda não tem acesso ao Atendabot. Contrate para liberar reservas por conversa.
                  </p>
                  <p className="text-[11px] text-white/80">
                    Com o Atendabot ativo, o cliente pode reservar direto no chat e o Reservo segue como base de disponibilidade.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setCheckoutProductKey('atendabot')}
                      className="rounded-lg border border-[#74c79b]/55 bg-[#2b2d8f]/55 px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#2b2d8f]/75 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Ativar Atendabot
                    </button>
                    <button
                      type="button"
                      disabled={!hasAtendabotWhatsappContractLink}
                      onClick={() => openExternalLink(ATENDABOT_WHATSAPP_CONTRACT_URL)}
                      className="ml-auto rounded-lg border border-[#74c79b]/55 bg-[#2b2d8f]/55 px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#2b2d8f]/75 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {hasAtendabotWhatsappContractLink
                        ? 'Falar no WhatsApp'
                        : 'Falar no WhatsApp (Indisponível)'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>Feedback Hero + Reservo</h3>

            <div
              className="rounded-2xl border border-white/10 p-5 text-white shadow-2xl"
              style={{ background: 'linear-gradient(135deg, #143023 0%, #1d5a3f 100%)' }}
            >
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-shrink-0 rounded-2xl border border-[#01E169]/40 bg-[#143023] p-2 shadow-lg">
                  <img
                    src={PRODUCT_IMAGES.feedbackHero}
                    alt="FeedbackHero"
                    className="h-16 w-16 rounded-xl bg-transparent p-1 object-contain"
                  />
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-sm font-semibold">Feedback Hero integrado ao Reservo</p>
                  <p className="text-xs text-white/90">
                    Quando ativo, o Reservo pede avaliação por estrelas no WhatsApp um dia após a reserva.
                  </p>
                  <p className="text-[11px] text-white/80">
                    As notas entram no FeedbackHero com origem &quot;reservo&quot; para separar dos demais canais.
                  </p>
                </div>
                {hasFeedbackHeroProduct ? (
                  <button
                    type="button"
                    onClick={() => navigate('/feedback-hero')}
                    className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-xl border border-[#01E169]/45 bg-[#0b1f15]/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-[#0b1f15]/90"
                  >
                    <Megaphone size={14} />
                    Abrir FeedbackHero
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setCheckoutProductKey('feedback_hero')}
                    className="ml-auto inline-flex shrink-0 items-center rounded-xl border border-[#01E169]/45 bg-[#0b1f15]/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-[#0b1f15]/90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Ativar FeedbackHero
                  </button>
                )}
              </div>

              {hasFeedbackHeroProduct && (
                <div className="mt-5 rounded-xl border border-white/20 bg-black/15 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold">Pedir avaliação das reservas realizadas</span>
                      <span className="mt-1 block text-xs text-white/85">
                        Quando ativo, o cliente recebe botões de 1 a 5 estrelas no WhatsApp do Reservo no dia seguinte à reserva.
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={settings.usar_feedbackhero_avaliacao_reservas}
                        aria-label="Pedir avaliação das reservas realizadas"
                        onClick={() => handleToggleChange('usar_feedbackhero_avaliacao_reservas', !settings.usar_feedbackhero_avaliacao_reservas)}
                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
                          settings.usar_feedbackhero_avaliacao_reservas ? 'bg-emerald-400' : 'bg-white/30'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out mt-0.5 ${
                            settings.usar_feedbackhero_avaliacao_reservas ? 'translate-x-[1.375rem]' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        settings.usar_feedbackhero_avaliacao_reservas
                          ? 'bg-emerald-200 text-emerald-900'
                          : 'bg-slate-700 text-slate-100'
                      }`}>
                        {settings.usar_feedbackhero_avaliacao_reservas ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {!hasFeedbackHeroProduct && (
                <div className="mt-4 space-y-3 rounded-xl border border-white/20 bg-black/15 p-4">
                  <p className="text-xs text-white/90">
                    Este restaurante ainda não tem acesso ao FeedbackHero. Contrate para liberar avaliação automática das reservas.
                  </p>
                  <p className="text-[11px] text-white/80">
                    Com o FeedbackHero ativo, as respostas por estrelas ficam centralizadas no painel com origem do Reservo.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setCheckoutProductKey('feedback_hero')}
                      className="rounded-lg border border-white/55 bg-[#143023]/45 px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#143023]/65 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Ativar FeedbackHero
                    </button>
                    <button
                      type="button"
                      disabled={!hasFeedbackHeroWhatsappContractLink}
                      onClick={() => openExternalLink(FEEDBACKHERO_WHATSAPP_CONTRACT_URL)}
                      className="ml-auto rounded-lg border border-white/55 bg-[#143023]/45 px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#143023]/65 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {hasFeedbackHeroWhatsappContractLink
                        ? 'Falar no WhatsApp'
                        : 'Falar no WhatsApp (Indisponível)'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="action-buttons">
            <button
              type="button"
              className="action-button"
              onClick={() => void handleSave()}
              disabled={saving}
            >
              {saving ? 'Salvando…' : 'Salvar configurações'}
            </button>
          </div>
        </div>
      </section>

      {occupancyReleaseConfirmation && (
        <div
          className="reservation-modal-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="settings-release-occupancies-title"
          aria-describedby="settings-release-occupancies-description"
          onClick={(event) => {
            if (event.target === event.currentTarget && !saving) {
              setOccupancyReleaseConfirmation(null);
            }
          }}
        >
          <div className="reservation-modal-content reservation-modal-content-confirmation">
            <button
              type="button"
              className="reservation-modal-close"
              onClick={() => setOccupancyReleaseConfirmation(null)}
              aria-label="Fechar confirmação"
              disabled={saving}
            >
              ×
            </button>
            <h2 id="settings-release-occupancies-title" className="reservation-modal-title">
              {occupancyReleaseConfirmation.source === 'mesas'
                ? 'Liberar todas as mesas?'
                : 'Liberar todas as cadeiras ocupadas?'}
            </h2>
            <div className="reservation-modal-form">
              <p id="settings-release-occupancies-description" className="reservation-modal-copy">
                Existem <strong>{occupancyReleaseConfirmation.count}</strong>{' '}
                {occupancyReleaseConfirmation.count === 1 ? 'ocupação ativa' : 'ocupações ativas'} de{' '}
                {occupancyReleaseConfirmation.source}. Para alterar o modo de operação, elas precisam ser encerradas.
              </p>
              <p className="reservation-modal-hint">
                {occupancyReleaseConfirmation.source === 'mesas'
                  ? (
                    'Reservas com clientes já sentados serão concluídas. Mesas apenas selecionadas, '
                    + 'que ainda aguardam o cliente, serão liberadas sem concluir a reserva.'
                  )
                  : 'Todos os grupos sentados serão levantados e as reservas correspondentes serão concluídas.'}
              </p>
              <div className="reservo-button-inline">
                <button
                  className="action-button"
                  type="button"
                  onClick={() => void handleSave(true)}
                  disabled={saving}
                >
                  <span>
                    {saving ? 'Liberando...' : 'Liberar todas e alterar modo'}
                  </span>
                </button>
                <button
                  className="action-button"
                  type="button"
                  onClick={() => setOccupancyReleaseConfirmation(null)}
                  disabled={saving}
                >
                  <span>Cancelar</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBlockManager && (
        <BlockManagementModal
          selectedDate={today}
          onClose={() => setShowBlockManager(false)}
          onChange={() => {}}
        />
      )}
      <ProductCheckoutModal
        open={Boolean(checkoutProductKey)}
        initialProductKey={checkoutProductKey}
        title="Ativar produtos"
        onClose={() => setCheckoutProductKey(null)}
      />
    </div>
  );
};

export default SettingsPage;
